<template>
  <div class="t-t-settings">
    <menu-content-container
      :header="$t('forms.settings_preview')"
      border-full
      class="relative"
    >
      <template v-if="openAiStore.openAiEnabled" #header-action>
        <TButton
          v-tooltip="{
            content: $t('forms.generate_preheader'),
          }"
          data-testid="generate-preheader"
          no-color-svg
          @click="generatePreheader()"
        >
          <template #icon>
            <MagicPen />
          </template>
        </TButton>
      </template>
      <t-text-area
        v-model="preview"
        data-testid="invisible-preheader"
        :label="$t('forms.settings_preview_text')"
        :rows="3"
      ></t-text-area>
      <div
        v-if="preheaderGenerating"
        class="absolute left-0 top-[-12px] flex h-[calc(100%+12px)] w-full items-center justify-center bg-black/40"
      >
        <TLoadingSpinner large />
      </div>
    </menu-content-container>
    <menu-content-container
      v-if="layoutStore.enableSubjectLine"
      :header="$t('forms.settings_subject')"
      border-full
    >
      <t-text-area
        v-model="subject"
        data-testid="email-subject"
        :label="$t('forms.settings_subject_text')"
        :rows="2"
      ></t-text-area>
      <t-note class="mt-2">{{ $t("forms.settings_subject_note") }}</t-note>
    </menu-content-container>
    <!-- <menu-content-container
      v-if="addGooglePromotional"
      border-full
      :margin-top="20"
    >
      <GooglePromotionalAnnotations />
    </menu-content-container> -->
    <menu-content-container
      :header="$t('forms.settings_email')"
      :header-margin-bottom="0"
      border-full
    >
      <t-note class="mb-2">{{ $t("forms.email_size_note") }}</t-note>
      <t-input-number
        v-model="width"
        data-testid="email-width"
        unit=""
        :min="320"
        :max="1920"
        regex-pattern="[0,9]{3,4}"
        :label="$t('forms.email_size')"
        :changed="widthValues?.wasChanged"
        flex
        @reset="width = widthValues?.defaultValue"
      ></t-input-number>
    </menu-content-container>
    <menu-content-container :header="$t('forms.settings_colors')" border-full>
      <content-row class="!items-end">
        <color-picker
          data-testid="background-color"
          :label="$t('forms.settings_bgc')"
          position="left"
          :default-color="bgColor"
          :changed="bgColorChanged"
          :reset-tooltip="$t('forms.reset_value')"
          @color-change="onBgColorChange"
          @reset="onBgColorChange(defaultBgColor)"
        ></color-picker>
        <TButton
          v-if="!bgGradient"
          data-testid="add-bg-gradient"
          @click="addGradient()"
        >
          <template #icon>
            <svg
              width="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19.958 3C20.3669 3 20.7597 3.16205 21.0488 3.45117C21.3379 3.74029 21.5 4.13312 21.5 4.54199V19.958C21.5 20.3669 21.3379 20.7597 21.0488 21.0488C20.7597 21.3379 20.3669 21.5 19.958 21.5H4.54199C4.13312 21.5 3.74029 21.3379 3.45117 21.0488C3.16205 20.7597 3 20.3669 3 19.958V4.54199C3 4.13312 3.16205 3.74029 3.45117 3.45117C3.74029 3.16205 4.13312 3 4.54199 3H19.958ZM10.708 4.54199V7.625H7.625V10.708H10.708V13.792H7.625V16.875H10.708V19.958H13.792V16.875H16.875V19.958H19.958V4.54199H16.875V7.625H13.792V4.54199H10.708ZM13.792 16.875H10.708V13.792H13.792V16.875ZM16.875 13.792H13.792V10.708H16.875V13.792ZM13.792 10.708H10.708V7.625H13.792V10.708Z"
                fill="currentColor"
              />
            </svg>
          </template>
          {{ $t("forms.add_gradient") }}
        </TButton>
      </content-row>
      <content-row v-if="bgGradient" class="!block">
        <TTabsToggle
          :header="$t('forms.gradient')"
          data-testid="bg-gradient-settings"
          default-open
          no-move
          show-remove
          @remove-block="bgGradient = undefined"
        >
          <menu-content-container border-full :padding-left-right="8">
            <TGradient v-model="bgGradient" />
          </menu-content-container>
        </TTabsToggle>
      </content-row>

      <content-row>
        <color-picker
          data-testid="link-color"
          :label="$t('forms.settings_link_color')"
          position="left"
          :default-color="linksColor"
          :changed="linkColorValues?.wasChanged"
          :reset-tooltip="$t('forms.reset_value')"
          @color-change="onLinksColorChange"
          @reset="onLinksColorChange(linkColorValues?.defaultValue)"
        ></color-picker>
      </content-row>

      <content-row :margin-bottom="16">
        <t-note>{{ $t("layout.background_gradient_text") }}</t-note>
      </content-row>
    </menu-content-container>
    <menu-content-container :header="$t('forms.settings_fonts')" border-full>
      <content-row class="!block" :margin-bottom="20">
        <font-size-color
          data-testid="font-size-color-h1"
          :label="$t('forms.settings_heading1')"
          tag="h1"
        ></font-size-color>
      </content-row>
      <content-row class="!block" :margin-bottom="20">
        <font-size-color
          data-testid="font-size-color-h2"
          :label="$t('forms.settings_heading2')"
          tag="h2"
        ></font-size-color>
      </content-row>
      <content-row class="!block" :margin-bottom="20">
        <font-size-color
          data-testid="font-size-color-h3"
          :label="$t('forms.settings_heading3')"
          tag="h3"
        ></font-size-color>
      </content-row>
      <content-row class="!block" :margin-bottom="20">
        <font-size-color
          data-testid="font-size-color-p"
          :label="$t('forms.settings_paragraph')"
          tag="p"
        ></font-size-color>
      </content-row>
    </menu-content-container>

    <menu-content-container
      :header="$t('forms.settings_line_height')"
      border-full
    >
      <content-row>
        <TButtonGroup :label="$t('forms.settings_line_heigh_predefined')">
          <TGroupButton
            data-testid="spacing-s"
            tooltip="1.2"
            small
            :active="spacing === 1.2"
            @button-clicked="lineHeightS"
          >
            <LineHeight1 />
          </TGroupButton>
          <TGroupButton
            data-testid="spacing-m"
            tooltip="1.5"
            small
            :active="spacing === 1.5"
            @button-clicked="lineHeightM"
          >
            <LineHeight15 />
          </TGroupButton>
          <TGroupButton
            data-testid="spacing-l"
            tooltip="1.8"
            small
            :active="spacing === 1.8"
            @button-clicked="lineHeightL"
          >
            <LineHeight18 />
          </TGroupButton>
          <TGroupButton
            data-testid="spacing-xl"
            tooltip="2"
            small
            :active="spacing === 2"
            @button-clicked="lineHeightXL"
          >
            <LineHeight2 />
          </TGroupButton>
        </TButtonGroup>
        <t-input-number
          v-model="lineHeight"
          data-testid="line-height-precise"
          :min="1.0"
          :max="4.0"
          unit=""
          step="0.01"
          regex-pattern="[0,9]{3,4}"
          :label="$t('forms.settings_line_heigh_precise')"
          :changed="lineHeightValues?.wasChanged"
          @reset="lineHeight = lineHeightValues?.defaultValue"
        ></t-input-number>
      </content-row>
    </menu-content-container>

    <ListStylingForm />

    <menu-content-container
      :header="$t('forms.settings_element_defaults')"
      :header-margin-bottom="0"
      :border-bottom="false"
    >
      <t-note class="mb-2">{{
        $t("forms.settings_element_defaults_note")
      }}</t-note>
    </menu-content-container>
    <div class="space-y-3 px-2">
      <SectionSettings />
      <ColumnSettings />
      <ButtonSettings />
      <TextSettings />
      <DividerSettings />
      <SpacerSettings />
      <ImageSettings />
      <SocialSettings />
      <GifSettings />
      <RatingSettings v-if="betaFeaturesStore.rating" />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import FontSizeColor from "./components/FontSizeColor/FontSizeColor.vue";
  import ListStylingForm from "./components/ListStylingForm/ListStylingForm.vue";
  import ButtonSettings from "./components/SettingsByElement/ButtonSettings.vue";
  import ColumnSettings from "./components/SettingsByElement/ColumnSettings.vue";
  import DividerSettings from "./components/SettingsByElement/DividerSettings.vue";
  import ImageSettings from "./components/SettingsByElement/ImageSettings.vue";
  import SocialSettings from "./components/SettingsByElement/SocialSettings.vue";
  import GifSettings from "./components/SettingsByElement/GifSettings.vue";
  import RatingSettings from "./components/SettingsByElement/RatingSettings.vue";
  import SectionSettings from "./components/SettingsByElement/SectionSettings.vue";
  import TextSettings from "./components/SettingsByElement/TextSettings.vue";
  import SpacerSettings from "./components/SettingsByElement/SpacerSettings.vue";
  import colorPicker from "./elements/colorPicker.vue";
  import TGradient from "@/components/TGradient/TGradient.vue";

  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useLayoutStore } from "@/stores/layoutStore";
  import { useOpenAiStore } from "@/stores/openAiStore";
  import { useBetaFeaturesStore } from "@/stores/betaFeatures";
  // import { useGooglePromotionalStore } from "@/stores/googlePromotionalStore";
  import useAiPreheader from "@/composables/text-generator/useAiPreheader";
  import {
    LineHeight1,
    LineHeight15,
    LineHeight18,
    LineHeight2,
    MagicPen,
  } from "@topol/icons";
  import {
    TInputNumber,
    TButtonGroup,
    TGroupButton,
    TTextArea,
    TNote,
    TButton,
    TLoadingSpinner,
    TTabsToggle,
  } from "@topol/twig-ui";

  import { computed } from "vue";
  import { templateSettings } from "@/store/templateSetting";
  // const GooglePromotionalAnnotations = defineAsyncComponent(
  //   () =>
  //     import(
  //       "@/forms/components/GooglePromotionalAnnotations/GooglePromotionalAnnotations.vue"
  //     )
  // );

  const mjmlStore = useMjmlStore();
  const layoutStore = useLayoutStore();
  const openAiStore = useOpenAiStore();
  const betaFeaturesStore = useBetaFeaturesStore();
  // const googlePromotionalStore = useGooglePromotionalStore();

  // const { addGooglePromotional } = googlePromotionalStore;
  const { generatePreheader, preheaderGenerating } = useAiPreheader();

  const width = computed({
    get() {
      return mjmlStore.getBodyWidth;
    },
    set(val: number) {
      mjmlStore.setBodyWidth(val);
    },
  });

  const defaultBgColor = templateSettings.templateBackgroundColor ?? "#FFFFFF";

  const bgColor = computed(() => {
    return mjmlStore.getBodyBg;
  });

  const bgColorChanged = computed(() => {
    return bgColor.value !== undefined && bgColor.value !== defaultBgColor;
  });

  const widthValues = computed(() => {
    const value = mjmlStore.getBodyWidth;
    const defaultValue = templateSettings.containerWidth;
    return {
      value,
      wasChanged: value !== undefined && value !== defaultValue,
      defaultValue,
    };
  });

  const linkColorValues = computed(() =>
    mjmlStore.getStyleWithDefault("a", "color")
  );

  const lineHeightValues = computed(() =>
    mjmlStore.getBaseAttrWithDefault("mj-text", "line-height")
  );

  const bgGradient = computed({
    get() {
      return mjmlStore.getStyle("body>div", "background-image");
    },
    set(val?: string) {
      mjmlStore.setStyle({
        tag: "body>div",
        attr: "background-image",
        value: val,
      });
    },
  });

  const lineHeight = computed({
    get() {
      return mjmlStore.getBaseAttrString("mj-text", "line-height");
    },
    set(val: number) {
      val = Math.round(val * 100) / 100;
      mjmlStore.setBaseAttr({
        block: "mj-text",
        prop: "line-height",
        value: val,
      });
      mjmlStore.updateBlockAttrGlobally({
        block: "mj-text",
        prop: "line-height",
        value: val,
      });
    },
  });

  const linksColor = computed(() => {
    const color = mjmlStore.getStyle("a", "color");

    if (color) {
      return color;
    } else {
      onLinksColorChange("#0000EE");

      return "#0000EE";
    }
  });

  const spacing = computed({
    get() {
      return parseFloat(mjmlStore.getBaseAttrString("mj-text", "line-height"));
    },
    set(val: number) {
      mjmlStore.setBaseAttr({
        block: "mj-text",
        prop: "line-height",
        value: val,
      });
    },
  });

  const preview = computed({
    get() {
      return mjmlStore.getTranslatableBaseAttr("mj-preview", "text");
    },
    set(val: string) {
      mjmlStore.setBaseAttrTranslation({
        block: "mj-preview",
        prop: "text",
        value: val,
      });
    },
  });

  const subject = computed({
    get() {
      return mjmlStore.getTranslatableBaseAttr("mj-subject", "text");
    },
    set(val: string) {
      mjmlStore.setBaseAttrTranslation({
        block: "mj-subject",
        prop: "text",
        value: val,
      });
    },
  });

  function onBgColorChange(color: string) {
    mjmlStore.setBodyBg({
      value: color,
    });
  }

  function onLinksColorChange(color: string) {
    mjmlStore.setStyle({
      tag: "a",
      attr: "color",
      value: color,
    });
  }

  function lineHeightS() {
    mjmlStore.setBaseAttr({
      block: "mj-text",
      prop: "line-height",
      value: 1.2,
    });
    mjmlStore.updateBlockAttrGlobally({
      block: "mj-text",
      prop: "line-height",
      value: 1.2,
    });
  }

  function lineHeightM() {
    mjmlStore.setBaseAttr({
      block: "mj-text",
      prop: "line-height",
      value: 1.5,
    });
    mjmlStore.updateBlockAttrGlobally({
      block: "mj-text",
      prop: "line-height",
      value: 1.5,
    });
  }

  function lineHeightL() {
    mjmlStore.setBaseAttr({
      block: "mj-text",
      prop: "line-height",
      value: 1.8,
    });
    mjmlStore.updateBlockAttrGlobally({
      block: "mj-text",
      prop: "line-height",
      value: 1.8,
    });
  }

  function lineHeightXL() {
    mjmlStore.setBaseAttr({
      block: "mj-text",
      prop: "line-height",
      value: 2,
    });
    mjmlStore.updateBlockAttrGlobally({
      block: "mj-text",
      prop: "line-height",
      value: 2,
    });
  }

  function addGradient() {
    bgGradient.value = "linear-gradient(90deg, #ff7e5f 0%, #feb47b 100%)";
  }
</script>
